import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  BookOpen,
  GraduationCap,
  HelpCircle,
  Loader2,
  LogOut,
  Send,
  Sparkles,
  Timer,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { useAuth } from '@/lib/auth-store';
import { useCourseDetailQuery } from '@/hooks/queries/useCourseDetail';
import {
  api,
  ApiError,
  type StudyMessageShape,
  type StudyMode,
  type StudySessionShape,
  type TutorSourceShape,
} from '@/lib/api';
import { cn } from '@/lib/utils';

/**
 * Phase-6 **Study Room** — course-scoped, persistent, hint-first adaptive
 * tutor with time-on-task tracking that flows back to the gradebook.
 *
 * Distinct from `/tutor` (the stateless one-shot Tutoring Center) on three
 * axes: (a) the transcript persists in the DB so students can resume after
 * closing the tab, (b) every turn is course-grounded RAG (the FTS5 index
 * from migration 0008), (c) student picks a mode per turn — hint /
 * walkthrough / answer — which steers the system prompt. Time spent
 * actively in the room (capped at 5-min idle per turn) gets credited to
 * `course_enrollments.study_time_ms` when the session ends.
 *
 * URL: `/c/:orgSlug/:courseSlug/study`
 */

const MODE_OPTIONS: Array<{ value: StudyMode; label: string; help: string }> = [
  { value: 'hint', label: 'Hint', help: 'Smallest nudge — keep me thinking' },
  { value: 'walkthrough', label: 'Walkthrough', help: 'Step by step, with checks' },
  { value: 'answer', label: 'Show me', help: 'I tried — show + explain' },
];

export function StudyRoom() {
  const { state } = useAuth();
  const navigate = useNavigate();
  const { orgSlug = '', courseSlug = '' } = useParams();

  const token = state.status === 'authenticated' ? state.token : '';
  const courseQuery = useCourseDetailQuery(token, orgSlug, courseSlug);
  const course = courseQuery.data?.course;

  const [session, setSession] = useState<StudySessionShape | null>(null);
  const [messages, setMessages] = useState<StudyMessageShape[]>([]);
  const [draft, setDraft] = useState('');
  const [mode, setMode] = useState<StudyMode>('hint');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ending, setEnding] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  // Once we've kicked off `start` for this course we don't want to re-fire
  // on re-renders (StrictMode in dev double-invokes effects).
  const startedFor = useRef<string | null>(null);

  // Boot — open or resume the session as soon as we have a course id.
  useEffect(() => {
    if (state.status !== 'authenticated' || !course) return;
    if (startedFor.current === course.id) return;
    startedFor.current = course.id;
    api.studyRoom
      .start(token, { courseId: course.id })
      .then(({ session: s, messages: m }) => {
        setSession(s);
        setMessages(m);
      })
      .catch((err) => {
        console.error('[study-room] start failed', err);
        setError("Couldn't start your study session. Try refreshing.");
      });
  }, [state, course]);

  // Auto-scroll to the newest turn.
  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
  }, [messages.length, sending]);

  // Best-effort: when the user navigates away or closes the tab, end the
  // session so its active_ms gets credited to the gradebook. The browser
  // doesn't guarantee `unload` fires on tab close, but most flows give us
  // a chance to flush.
  useEffect(() => {
    function flush() {
      if (!session || session.endedAt) return;
      // sendBeacon-style fetch with keepalive so it survives navigation.
      void fetch(
        `${import.meta.env.VITE_API_BASE_URL ?? '/api'}/ai/study-session/${session.id}/end`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,
        },
      ).catch(() => {
        /* best-effort */
      });
    }
    window.addEventListener('beforeunload', flush);
    return () => {
      window.removeEventListener('beforeunload', flush);
      flush();
    };
  }, [session, token]);

  if (state.status !== 'authenticated') return null;

  async function send(e: FormEvent) {
    e.preventDefault();
    const question = draft.trim();
    if (!question || sending || !session) return;
    setError(null);
    setSending(true);
    setDraft('');

    // Optimistic — push the user turn right away so the UI feels alive.
    setMessages((m) => [
      ...m,
      {
        id: `pending-${Date.now()}`,
        role: 'user',
        content: question,
        mode,
        sources: null,
        createdAt: Date.now(),
      },
    ]);

    try {
      const res = await api.studyRoom.turn(token, session.id, { question, mode });
      setSession(res.session);
      // Replace the optimistic user turn + append assistant turn from the
      // server response. We don't get message ids back from /turn (server
      // creates them); easiest is a re-fetch when ordering matters, but for
      // now an in-place assistant append is good enough and avoids an extra
      // round-trip.
      setMessages((m) => {
        const stable = m.filter((x) => !x.id.startsWith('pending-'));
        return [
          ...stable,
          {
            id: `local-user-${Date.now()}`,
            role: 'user',
            content: question,
            mode,
            sources: null,
            createdAt: Date.now(),
          },
          {
            id: `local-asst-${Date.now()}`,
            role: 'assistant',
            content: res.reply,
            mode: null,
            sources: res.sources && res.sources.length ? res.sources : null,
            createdAt: Date.now(),
          },
        ];
      });
    } catch (err) {
      // Roll back the optimistic user turn so the student can retry.
      setMessages((m) => m.filter((x) => !x.id.startsWith('pending-')));
      setDraft(question);
      if (err instanceof ApiError && err.code === 'ai_access_denied') {
        setError("You're out of AI credits for this period.");
      } else if (err instanceof ApiError && err.code === 'session_ended') {
        setError('This session has already ended. Start a new one.');
      } else {
        setError("Couldn't reach the tutor. Try again in a moment.");
      }
    } finally {
      setSending(false);
    }
  }

  async function endNow() {
    if (!session || ending) return;
    setEnding(true);
    try {
      await api.studyRoom.end(token, session.id);
    } catch (err) {
      console.error('[study-room] end failed', err);
    } finally {
      setEnding(false);
      navigate(`/c/${orgSlug}/${courseSlug}`);
    }
  }

  const activeLabel = useMemo(() => formatDuration(session?.activeMs ?? 0), [session?.activeMs]);

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />

      <main className="container flex flex-1 flex-col gap-4 py-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div className="space-y-1">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              <Link to="/dashboard" className="hover:text-foreground">
                Dashboard
              </Link>
              <span className="mx-1">/</span>
              <Link to={`/c/${orgSlug}/${courseSlug}`} className="hover:text-foreground">
                {course?.title ?? courseSlug}
              </Link>
              <span className="mx-1">/</span>
              <span>Study Room</span>
            </div>
            <h1 className="flex items-center gap-2 font-display text-3xl font-bold tracking-tight">
              <GraduationCap className="h-7 w-7 text-accent" /> Study Room
            </h1>
            <p className="max-w-2xl text-sm text-muted-foreground">
              EduBorg pulls relevant lessons from {course?.title ?? 'your course'} into the
              conversation. Pick a mode per question — Hint when you want to keep thinking,
              Walkthrough when you're stuck, Show me only after you've tried.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm">
              <Timer className="h-4 w-4 text-accent" aria-hidden />
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Active</span>
              <span className="font-medium tabular-nums">{activeLabel}</span>
            </span>
            <button
              type="button"
              onClick={endNow}
              disabled={ending || !session}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-secondary disabled:opacity-50"
              title="End session and credit your study time to the gradebook"
            >
              {ending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4" />
              )}
              End session
            </button>
          </div>
        </header>

        <section className="flex flex-1 flex-col rounded-2xl border border-border bg-card">
          <div ref={scrollerRef} className="flex-1 space-y-3 overflow-y-auto p-5">
            {messages.length === 0 && !sending ? (
              <EmptyStudyRoom />
            ) : (
              messages.map((m) => (
                <article
                  key={m.id}
                  className={cn(
                    'max-w-[85%] rounded-2xl px-4 py-3 text-sm',
                    m.role === 'user'
                      ? 'ml-auto bg-primary text-primary-foreground'
                      : 'bg-secondary text-foreground',
                  )}
                >
                  {m.role === 'user' && m.mode && (
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground/70">
                      {m.mode}
                    </div>
                  )}
                  <div className="whitespace-pre-wrap break-words">{m.content}</div>
                  <SourceList sources={m.sources} />
                </article>
              ))
            )}
            {sending && (
              <div className="text-xs italic text-muted-foreground">EduBorg is thinking…</div>
            )}
          </div>

          <form
            onSubmit={send}
            className="flex flex-col gap-2 border-t border-border bg-card/50 p-3"
          >
            {/* Mode picker — segmented control. Hint is the default. */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <HelpCircle className="h-3 w-3" aria-hidden /> Mode
              </span>
              {MODE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setMode(opt.value)}
                  title={opt.help}
                  className={cn(
                    'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                    mode === opt.value
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-foreground hover:bg-secondary/70',
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex items-end gap-2">
              <textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={`Ask EduBorg about ${course?.title ?? 'your course'}.`}
                rows={2}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    void send(e as unknown as FormEvent);
                  }
                }}
                className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
              <button
                type="submit"
                disabled={!draft.trim() || sending || !session}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                Ask
              </button>
            </div>
          </form>

          {error && (
            <div className="border-t border-destructive/30 bg-destructive/5 px-4 py-2 text-xs text-destructive">
              {error}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}

function SourceList({ sources }: { sources: TutorSourceShape[] | null }) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className="mt-3 space-y-1.5 border-t border-border/40 pt-2">
      <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <BookOpen className="h-3 w-3" aria-hidden />
        From your course
      </div>
      <ul className="space-y-1">
        {sources.map((s, j) => (
          <li key={s.blockId} className="text-[11px] text-muted-foreground">
            <span className="font-medium text-foreground">
              [{j + 1}] {s.lessonTitle}
            </span>
            <span className="ml-1.5">— {s.snippet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyStudyRoom() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
      <Sparkles className="h-8 w-8 text-accent" aria-hidden />
      <p className="max-w-md text-sm text-muted-foreground">
        Pick a mode and ask. Try "give me a hint on the polynomials problem in lesson 4" or "walk me
        through photosynthesis."
      </p>
    </div>
  );
}

/** ms → "1h 23m" or "12m 34s" or "45s". */
function formatDuration(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}
